import { useEffect, useMemo, useState } from "react";
import {
  GameSocket,
  clearStoredSession,
  createSession,
  loadStoredSession,
  rematchSession,
  saveStoredSession,
  startGame,
} from "./api";
import type { PublicSessionState, ServerMessage } from "./types";
import { Banner } from "./components/Banner";
import { Landing } from "./views/Landing";
import { HostNew } from "./views/HostNew";
import { JoinTeam } from "./views/JoinTeam";
import { Lobby } from "./views/Lobby";
import { Table } from "./views/Table";
import { AARView } from "./views/AAR";
import { Onboarding } from "./components/Onboarding";
import { EntryModal } from "./components/EntryModal";
import { playDice, playFail, playInject, playReveal, playSuccess, isMuted, setMuted } from "./audio";

type Stage =
  | { kind: "landing" }
  | { kind: "host-new"; sessionToken: string; imToken: string }
  | { kind: "join-team"; sessionToken: string }
  | { kind: "playing"; sessionToken: string; imToken?: string; teamToken?: string; handle?: string; teamRole?: "soc" | "ir-lead" | "comms" }
  | { kind: "error"; message: string };

export function App() {
  const [stage, setStage] = useState<Stage>(() => {
    // Auto-resume if stored. Otherwise check ?session= for a quick-join link.
    const stored = loadStoredSession();
    if (stored?.sessionToken) {
      return {
        kind: "playing",
        sessionToken: stored.sessionToken,
        teamToken: stored.teamToken,
        imToken: stored.imToken,
      };
    }
    const params = new URLSearchParams(location.search);
    const s = params.get("session");
    if (s) return { kind: "join-team", sessionToken: s };
    return { kind: "landing" };
  });

  const [state, setState] = useState<PublicSessionState | undefined>();
  const [lastRoll, setLastRoll] = useState<Extract<ServerMessage, { type: "rollResult" }>>();
  const [inject, setInject] = useState<Extract<ServerMessage, { type: "injectFired" }>>();
  const [socket, setSocket] = useState<GameSocket | undefined>();
  const [you, setYou] = useState<{ role: "host" | "im" | "player" | "spectator"; teamId?: string; playerId?: string }>();

  useEffect(() => {
    if (stage.kind !== "playing") return;
    const s = new GameSocket({
      sessionToken: stage.sessionToken,
      teamToken: stage.teamToken,
      imToken: stage.imToken,
      handle: stage.handle,
      teamRole: stage.teamRole,
      playerId: loadStoredSession()?.playerId,
      onMessage: (m) => {
        if (m.type === "snapshot") setState(m.state);
        else if (m.type === "rollResult") {
          setLastRoll(m);
          playDice();
          setTimeout(() => {
            if (m.revealed) playReveal();
            else if (m.success) playSuccess();
            else playFail();
          }, 140);
        } else if (m.type === "injectFired") {
          setInject(m);
          playInject();
          setTimeout(() => setInject(undefined), 60000);
        } else if (m.type === "hello-ack") {
          setYou(m.you);
          if (m.you.playerId) {
            saveStoredSession({
              sessionToken: stage.sessionToken,
              teamToken: stage.teamToken,
              imToken: stage.imToken,
              playerId: m.you.playerId,
            });
          }
        } else if (m.type === "rematch") {
          // Auto-switch every connected client to the new session.
          //
          // This used to match the mapping on `you?.teamId`, but `you` is set
          // by hello-ack after this closure was built and is not in the
          // effect's deps, so it read `undefined` every time: the mapping
          // never matched, and a player was switched into the new session
          // with `teamToken: undefined`, holding a seat with no credential.
          //
          // It does not need `you`. The server addresses this message per
          // socket (gameSession.handleRematch): a player receives only its own
          // team's entry, the IM receives every team's plus the new IM token.
          // So the single entry is ours whenever we hold a team token, and
          // `stage` is captured correctly because the tokens are in the deps.
          const mappedTeam = stage.teamToken ? m.mapping.teams[0] : undefined;

          // A player whose team did not survive the rematch must not be
          // dropped into the new session anyway: they would look like they
          // were playing until the first action failed. Send them through
          // join, where they pick a team and are issued a real token.
          if (stage.teamToken && !mappedTeam?.newTeamToken) {
            clearStoredSession();
            setState(undefined);
            setLastRoll(undefined);
            setYou(undefined);
            setStage({ kind: "join-team", sessionToken: m.mapping.newSessionToken });
            return;
          }

          const next: Stage = {
            kind: "playing",
            sessionToken: m.mapping.newSessionToken,
            // IM keeps role; the new IM token only ships in the IM's projection.
            imToken: m.mapping.newImToken ?? stage.imToken,
            teamToken: mappedTeam?.newTeamToken,
          };
          saveStoredSession({
            sessionToken: next.sessionToken,
            teamToken: next.teamToken,
            imToken: next.imToken,
          });
          setState(undefined);
          setLastRoll(undefined);
          setStage(next);
        } else if (m.type === "error") {
          console.warn("[bp]", m.code);
        }
      },
      onClose: ({ receivedAny, clean }) => {
        if (clean) return;
        if (!receivedAny) {
          // Handshake failed — most likely the stored session/team token
          // points at a DO that has been reset. Clear and return to landing
          // instead of looping forever.
          clearStoredSession();
          setState(undefined);
          setLastRoll(undefined);
          setYou(undefined);
          setStage({
            kind: "error",
            message:
              "couldn't connect — the session may have ended. start a new one or paste a fresh session token.",
          });
          return;
        }
        // We got at least one snapshot — transient drop, retry once.
        setTimeout(() => {
          if (stage.kind === "playing") setStage({ ...stage });
        }, 1500);
      },
    });
    s.connect();
    // The socket instance is stored for later send/close; this is the one
    // place it can be created. Storing it here is intentional.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSocket(s);
    return () => s.close();
    // re-run on stage identity change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.kind, stage.kind === "playing" && stage.sessionToken, stage.kind === "playing" && stage.teamToken, stage.kind === "playing" && stage.imToken]);

  const isIm = you?.role === "im";
  const isPlayer = you?.role === "player";

  const onCreate = async () => {
    try {
      // Server starts the session with `random` as a placeholder; the host
      // picks/swaps the real scenario from the HostNew screen before they
      // continue to the lobby.
      const r = await createSession();
      setStage({ kind: "host-new", sessionToken: r.sessionToken, imToken: r.imToken });
    } catch {
      setStage({ kind: "error", message: "could not create session" });
    }
  };

  const onJoinSession = (sessionToken: string) => {
    if (!sessionToken) return;
    setStage({ kind: "join-team", sessionToken });
  };

  const onContinueAsIm = () => {
    if (stage.kind !== "host-new") return;
    saveStoredSession({ sessionToken: stage.sessionToken, imToken: stage.imToken });
    setStage({ kind: "playing", sessionToken: stage.sessionToken, imToken: stage.imToken });
  };

  const onJoined = (teamToken: string, handle: string, teamRole: "soc" | "ir-lead" | "comms") => {
    if (stage.kind !== "join-team") return;
    saveStoredSession({ sessionToken: stage.sessionToken, teamToken });
    setStage({ kind: "playing", sessionToken: stage.sessionToken, teamToken, handle, teamRole });
  };

  const onStart = async (): Promise<string | undefined> => {
    if (stage.kind !== "playing" || !stage.imToken) return "not-im";
    try {
      await startGame(stage.sessionToken, stage.imToken);
      return undefined;
    } catch (e) {
      // Return the server code so the Lobby can show a useful message
      // instead of the click appearing to do nothing.
      return e instanceof Error ? e.message : "start-failed";
    }
  };

  const onRematch = async () => {
    if (stage.kind !== "playing" || !stage.imToken) return;
    try {
      // Fire-and-forget — the server broadcasts a `rematch` event that
      // every connected client (including the IM) handles uniformly.
      await rematchSession(stage.sessionToken, stage.imToken);
    } catch {
      /* ignore */
    }
  };

  const onLeave = () => {
    socket?.close();
    clearStoredSession();
    setState(undefined);
    setLastRoll(undefined);
    setStage({ kind: "landing" });
  };

  const banner = state?.banner ?? "BREAKPOINT // INCIDENT RESPONSE LIVE-FIRE";
  const [entryTab, setEntryTab] = useState<"host" | "join" | "im" | null>(null);
  const openEntry = (tab: "host" | "join" | "im") => setEntryTab(tab);
  const closeEntry = () => setEntryTab(null);

  const view = useMemo(() => {
    if (stage.kind === "landing") {
      return <Landing onOpenMenu={openEntry} />;
    }
    if (stage.kind === "host-new") {
      return <HostNew sessionToken={stage.sessionToken} imToken={stage.imToken} onContinueAsIm={onContinueAsIm} />;
    }
    if (stage.kind === "join-team") {
      return <JoinTeam sessionToken={stage.sessionToken} onJoined={onJoined} onBack={() => setStage({ kind: "landing" })} />;
    }
    if (stage.kind === "playing") {
      if (!state) return <div className="panel">Connecting…</div>;
      if (state.phase === "lobby")
        return (
          <Lobby
            state={state}
            isIm={isIm}
            sessionToken={stage.kind === "playing" ? stage.sessionToken : undefined}
            teamToken={stage.kind === "playing" ? stage.teamToken : undefined}
            onStart={onStart}
          />
        );
      if (state.phase === "playing")
        return (
          <Table
            state={state}
            lastRoll={lastRoll}
            isIm={isIm}
            isPlayer={isPlayer}
            myPlayerId={you?.playerId}
            onPlay={(cardId, predictStage) => socket?.send({ type: "playProcedure", cardId, predictStage })}
            onFireInject={(teamId) => socket?.send({ type: "fireInject", teamId, injectId: "auto" })}
            onChat={(text) => socket?.send({ type: "chat", text })}
            onUsePower={() => socket?.send({ type: "useRolePower" })}
          />
        );
      return (
        <AARView
          state={state}
          isIm={isIm}
          onRematch={onRematch}
          onVoteRematch={() => socket?.send({ type: "requestRematch" })}
        />
      );
    }
    if (stage.kind === "error") {
      return (
        <section className="panel" style={{ maxWidth: 540 }}>
          <h2>Connection problem</h2>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-muted)" }}>
            {stage.message}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="primary" onClick={() => setStage({ kind: "landing" })}>
              ← Back to start
            </button>
          </div>
        </section>
      );
    }
    return <div className="panel">Unknown state.</div>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, state, lastRoll, isIm, isPlayer]);

  const [showOnboarding, setShowOnboarding] = useState(true);
  const [muted, setMutedState] = useState(isMuted());
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  return (
    <div className="app">
      <Banner
        banner={banner}
        scenarioName={state?.scenarioName}
        sessionFingerprint={state?.sessionFingerprint}
        onMenu={stage.kind === "landing" ? openEntry : undefined}
      />
      <main className="main">{view}</main>
      <footer className="footer">
        <span>do not paste real IoCs or production secrets</span>
        <span>
          {you ? `connected as ${you.role}` : "—"} ·{" "}
          <button className="linkbtn" onClick={toggleMute} aria-pressed={muted}>
            {muted ? "🔇 sound off" : "🔊 sound on"}
          </button>
          {" · "}
          <button className="linkbtn" onClick={onLeave}>leave</button>
        </span>
      </footer>

      {inject && <InjectToast inject={inject} onDismiss={() => setInject(undefined)} />}

      {stage.kind === "playing" && state?.phase === "lobby" && showOnboarding && you?.role === "player" && (
        <Onboarding onDismiss={() => setShowOnboarding(false)} />
      )}

      {entryTab && (
        <EntryModal
          initialTab={entryTab}
          onClose={closeEntry}
          onCreate={async () => {
            closeEntry();
            await onCreate();
          }}
          onJoin={(s) => {
            closeEntry();
            onJoinSession(s);
          }}
          onIm={(s, im) => {
            closeEntry();
            saveStoredSession({ sessionToken: s, imToken: im });
            setStage({ kind: "playing", sessionToken: s, imToken: im });
          }}
        />
      )}
    </div>
  );
}

function InjectToast({
  inject,
  onDismiss,
}: {
  inject: Extract<ServerMessage, { type: "injectFired" }>;
  onDismiss: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(60);
  useEffect(() => {
    if (secondsLeft <= 0) {
      onDismiss();
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onDismiss]);
  return (
    <div className="inject-toast" role="alert">
      <h4>Inject — {inject.injectName}</h4>
      <div>{inject.effect}</div>
      {inject.resolved && (
        <div style={{ marginTop: 6, color: "var(--color-accent)", fontFamily: "var(--font-mono)", fontSize: 12 }}>
          → {inject.resolved}
        </div>
      )}
      <div className="countdown">{secondsLeft}s</div>
      <div className="timer" style={{ animationDuration: "60s" }} />
    </div>
  );
}
